import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Users, Edit2, X, Save, MapPin, BadgeCheck, Trash2, Shield, Search, Filter, ChevronDown, ChevronLeft, ShoppingBag, Ticket, TrendingUp, Calendar, DollarSign, Trophy, Award, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { calculateLegacyScore } from '@/utils/scoreCalculator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PinKeypad } from '@/components/ui/pin-keypad';
import { PinDisplay } from '@/components/ui/pin-display';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MemberAchievementsView } from './achievements/MemberAchievementsView';

type TeamRole = 'admin' | 'vendedor';

interface TeamMember {
  user_id: string;
  nombre: string;
  username: string;
  pin: string;
  created_at: string;
  ubicacion_tienda: string | null;
  role: TeamRole;
}

interface MemberSale {
  id: string;
  product_name: string;
  category: string;
  original_price: number;
  final_price: number;
  savings: number;
  quantity: number;
  created_at: string;
  month_year: string;
  customer_name?: string;
}

interface MemberRedemption {
  id: string;
  benefit_type: string;
  quantity: number;
  created_at: string;
  month_year: string;
  notes: string | null;
  customer_name?: string;
}

interface MemberStats {
  totalSales: number;
  totalAmount: number;
  totalSavings: number;
  totalRedemptions: number;
}

const storeLocations = [
  'Santa Ana',
  'San Pedro',
  'Cartago',
  'Heredia Pasel Las Flores',
  'Heredia Fátima',
  'Bodega',
  'Paseo Metrópoli',
  'Paseo las Flores',
  'Mall San Pedro',
  'Santa Ana Town Center',
  'Megasuper Heredia Centro',
  'Vendedor comodín',
];

const roleLabels: Record<TeamRole, { label: string; color: string; bgColor: string; borderColor: string }> = {
  admin: { label: 'Administrador', color: 'text-amber-400', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/30' },
  vendedor: { label: 'Vendedor', color: 'text-purple-400', bgColor: 'bg-purple-500/20', borderColor: 'border-purple-500/30' },
};

// Validate name has at least 2 words
const validateName = (name: string): { valid: boolean; error?: string } => {
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const words = cleanName.split(' ').filter(w => w.length > 0);
  if (words.length < 2) {
    return { valid: false, error: 'Por favor ingresa nombre y apellido (ej: María López)' };
  }
  return { valid: true };
};

const formatCurrency = (amount: number) => {
  if (amount >= 1000000) {
    return `₡${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `₡${(amount / 1000).toFixed(0)}K`;
  }
  return `₡${amount}`;
};

export const TeamManagementSection: React.FC = () => {
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPin, setEditPin] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // New state for member details panel
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [memberSales, setMemberSales] = useState<MemberSale[]>([]);
  const [memberRedemptions, setMemberRedemptions] = useState<MemberRedemption[]>([]);
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState('ventas');
  
  // Ranking data for sellers - now stores full stats
  const [rankingData, setRankingData] = useState<Map<string, { rank: number; ventas: number; canjes: number; trainingCompleted: boolean; achievementPoints: number }>>(new Map());
  const [loadingRanking, setLoadingRanking] = useState(true);

  const fetchTeamMembers = async () => {
    try {
      // Fetch both admins and sellers
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('role', ['admin', 'vendedor']);

      if (rolesError) throw rolesError;

      if (roles && roles.length > 0) {
        const userIds = roles.map(r => r.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, nombre, username, pin, created_at, ubicacion_tienda')
          .in('user_id', userIds)
          .order('created_at', { ascending: false });

        if (profilesError) throw profilesError;

        // Map profiles with their roles
        const membersWithRoles: TeamMember[] = (profiles || []).map(profile => {
          const roleData = roles.find(r => r.user_id === profile.user_id);
          return {
            ...profile,
            role: (roleData?.role as TeamRole) || 'vendedor',
          };
        });

        // Sort: admins first, then by name
        membersWithRoles.sort((a, b) => {
          if (a.role === 'admin' && b.role !== 'admin') return -1;
          if (a.role !== 'admin' && b.role === 'admin') return 1;
          return a.nombre.localeCompare(b.nombre);
        });

        setTeamMembers(membersWithRoles);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch member details when selected
  const fetchMemberDetails = async (member: TeamMember) => {
    setLoadingDetails(true);
    try {
      // Fetch sales registered by this member
      const { data: sales, error: salesError } = await supabase
        .from('discount_purchases')
        .select('id, product_name, category, original_price, final_price, savings, quantity, created_at, month_year, user_id')
        .eq('registered_by', member.user_id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (salesError) throw salesError;

      // Get customer names for sales
      if (sales && sales.length > 0) {
        const customerIds = [...new Set(sales.map(s => s.user_id))];
        const { data: customerProfiles } = await supabase
          .from('profiles')
          .select('user_id, nombre')
          .in('user_id', customerIds);

        const salesWithNames: MemberSale[] = sales.map(sale => ({
          ...sale,
          customer_name: customerProfiles?.find(p => p.user_id === sale.user_id)?.nombre || 'Cliente',
        }));
        setMemberSales(salesWithNames);
      } else {
        setMemberSales([]);
      }

      // Fetch redemptions processed by this member
      const { data: redemptions, error: redemptionsError } = await supabase
        .from('benefit_redemptions')
        .select('id, benefit_type, quantity, created_at, month_year, notes, membership_id')
        .eq('redeemed_by', member.user_id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (redemptionsError) throw redemptionsError;

      // Get customer names for redemptions
      if (redemptions && redemptions.length > 0) {
        const membershipIds = [...new Set(redemptions.map(r => r.membership_id))];
        const { data: memberships } = await supabase
          .from('memberships')
          .select('id, user_id')
          .in('id', membershipIds);

        const userIds = memberships?.map(m => m.user_id) || [];
        const { data: customerProfiles } = await supabase
          .from('profiles')
          .select('user_id, nombre')
          .in('user_id', userIds);

        const redemptionsWithNames: MemberRedemption[] = redemptions.map(redemption => {
          const membership = memberships?.find(m => m.id === redemption.membership_id);
          const customer = customerProfiles?.find(p => p.user_id === membership?.user_id);
          return {
            ...redemption,
            customer_name: customer?.nombre || 'Cliente',
          };
        });
        setMemberRedemptions(redemptionsWithNames);
      } else {
        setMemberRedemptions([]);
      }

      // Calculate stats
      const totalSales = sales?.length || 0;
      const totalAmount = sales?.reduce((sum, s) => sum + s.final_price, 0) || 0;
      const totalSavings = sales?.reduce((sum, s) => sum + s.savings, 0) || 0;
      const totalRedemptions = redemptions?.reduce((sum, r) => sum + r.quantity, 0) || 0;

      setMemberStats({
        totalSales,
        totalAmount,
        totalSavings,
        totalRedemptions,
      });
    } catch (error) {
      console.error('Error fetching member details:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los detalles',
        variant: 'destructive',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fetch ranking data for sellers
  const fetchRankingData = async () => {
    setLoadingRanking(true);
    try {
      const { data, error } = await supabase.rpc('get_seller_ranking_stats');
      if (error) throw error;
      
      if (data && Array.isArray(data)) {
        const rankMap = new Map<string, { rank: number; ventas: number; canjes: number; trainingCompleted: boolean; achievementPoints: number }>();
        data.forEach((seller: { seller_id: string; total_ventas: number; total_canjes: number; training_completed: boolean; achievement_points: number }, index: number) => {
          rankMap.set(seller.seller_id, { 
            rank: index + 1, 
            ventas: seller.total_ventas || 0, 
            canjes: seller.total_canjes || 0,
            trainingCompleted: seller.training_completed || false,
            achievementPoints: seller.achievement_points || 0,
          });
        });
        setRankingData(rankMap);
      }
    } catch (error) {
      console.error('Error fetching ranking data:', error);
    } finally {
      setLoadingRanking(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
    fetchRankingData();
  }, []);

  // Fetch details when member is selected
  useEffect(() => {
    if (selectedMember) {
      fetchMemberDetails(selectedMember);
    }
  }, [selectedMember]);

  const handleSelectMember = (member: TeamMember) => {
    setSelectedMember(member);
    setDetailsTab('ventas');
  };

  const handleBackToList = () => {
    setSelectedMember(null);
    setMemberSales([]);
    setMemberRedemptions([]);
    setMemberStats(null);
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingId(member.user_id);
    setEditName(member.nombre);
    setEditPin('');
    setEditLocation(member.ubicacion_tienda || '');
  };

  const handleSaveEdit = async (userId: string) => {
    const member = teamMembers.find(m => m.user_id === userId);
    if (!member) return;

    // Validate name if changed
    const nameValidation = validateName(editName);
    if (!nameValidation.valid) {
      toast({
        title: 'Error',
        description: nameValidation.error,
        variant: 'destructive',
      });
      return;
    }

    // If PIN is entered, it must be 6 digits
    if (editPin && editPin.length !== 6) {
      toast({
        title: 'Error',
        description: 'El PIN debe tener 6 dígitos',
        variant: 'destructive',
      });
      return;
    }

    // Location required only for sellers
    if (member.role === 'vendedor' && !editLocation) {
      toast({
        title: 'Error',
        description: 'Seleccioná una ubicación',
        variant: 'destructive',
      });
      return;
    }

    setSavingEdit(true);
    try {
      const updateData: { nombre: string; pin?: string; ubicacion_tienda: string | null } = {
        nombre: editName.trim(),
        ubicacion_tienda: member.role === 'vendedor' ? editLocation : null,
      };
      
      // Only update PIN if a new one was entered
      if (editPin && editPin.length === 6) {
        updateData.pin = editPin;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('user_id', userId);

      if (error) throw error;

      const changes = [];
      if (editName.trim() !== member.nombre) changes.push('nombre');
      if (editPin) changes.push('PIN');
      if (editLocation !== member.ubicacion_tienda) changes.push('ubicación');

      toast({
        title: 'Usuario actualizado',
        description: changes.length > 0 ? `Actualizado: ${changes.join(', ')}` : 'Sin cambios',
      });

      setEditingId(null);
      setEditName('');
      setEditPin('');
      setEditLocation('');
      
      // Update selected member if it's the one being edited
      if (selectedMember?.user_id === userId) {
        const updatedMember = { ...selectedMember, nombre: editName.trim(), ubicacion_tienda: member.role === 'vendedor' ? editLocation : null };
        setSelectedMember(updatedMember);
      }
      
      fetchTeamMembers();
    } catch (error: any) {
      console.error('Error updating team member:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el usuario',
        variant: 'destructive',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteMember = async (member: TeamMember) => {
    setDeletingId(member.user_id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const response = await supabase.functions.invoke('delete-team-member', {
        body: { 
          userId: member.user_id,
          nombre: member.nombre,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Error al eliminar usuario');
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      toast({
        title: '¡Usuario eliminado!',
        description: `${member.nombre} ha sido eliminado permanentemente.`,
      });

      // Remove from local state
      setTeamMembers(teamMembers.filter(m => m.user_id !== member.user_id));
      
      // Clear selection if the deleted member was selected
      if (selectedMember?.user_id === member.user_id) {
        handleBackToList();
      }
    } catch (error: any) {
      console.error('Error deleting team member:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el usuario',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'todos' | TeamRole>('todos');
  const [locationFilter, setLocationFilter] = useState('todas');

  // Get unique locations from team members
  const uniqueLocations = useMemo(() => {
    const locations = teamMembers
      .filter(m => m.ubicacion_tienda)
      .map(m => m.ubicacion_tienda as string);
    return [...new Set(locations)].sort();
  }, [teamMembers]);

  // Helper to get ranking styles based on position
  const getRankingStyles = (rank: number) => {
    if (rank === 1) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
    if (rank === 2) return { bg: 'bg-slate-400/20', text: 'text-slate-300', border: 'border-slate-400/30' };
    if (rank === 3) return { bg: 'bg-amber-600/20', text: 'text-amber-500', border: 'border-amber-600/30' };
    return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
  };

  // Filtered and sorted team members
  const filteredMembers = useMemo(() => {
    return teamMembers
      .filter(member => {
        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesName = member.nombre.toLowerCase().includes(query);
          const matchesLocation = member.ubicacion_tienda?.toLowerCase().includes(query);
          if (!matchesName && !matchesLocation) return false;
        }
        // Role filter
        if (roleFilter !== 'todos' && member.role !== roleFilter) return false;
        // Location filter
        if (locationFilter !== 'todas') {
          if (locationFilter === 'sin-tienda') {
            if (member.ubicacion_tienda) return false;
          } else if (member.ubicacion_tienda !== locationFilter) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        // Sellers with ranking come first, sorted by rank (#1 first)
        const rankA = a.role === 'vendedor' ? (rankingData.get(a.user_id)?.rank ?? Infinity) : Infinity;
        const rankB = b.role === 'vendedor' ? (rankingData.get(b.user_id)?.rank ?? Infinity) : Infinity;
        
        // Both have rankings - sort by rank
        if (rankA !== Infinity && rankB !== Infinity) {
          return rankA - rankB;
        }
        
        // One has ranking, one doesn't - ranked first
        if (rankA !== Infinity && rankB === Infinity) return -1;
        if (rankA === Infinity && rankB !== Infinity) return 1;
        
        // Neither has ranking - admins last, then by name
        if (a.role === 'admin' && b.role !== 'admin') return 1;
        if (a.role !== 'admin' && b.role === 'admin') return -1;
        
        return a.nombre.localeCompare(b.nombre);
      });
  }, [teamMembers, searchQuery, roleFilter, locationFilter, rankingData]);

  const adminCount = teamMembers.filter(m => m.role === 'admin').length;
  const sellerCount = teamMembers.filter(m => m.role === 'vendedor').length;
  const filteredAdminCount = filteredMembers.filter(m => m.role === 'admin').length;
  const filteredSellerCount = filteredMembers.filter(m => m.role === 'vendedor').length;

  // Render member details panel
  const renderMemberDetails = () => {
    if (!selectedMember) return null;

    const roleInfo = roleLabels[selectedMember.role];
    const RoleIcon = selectedMember.role === 'admin' ? Shield : BadgeCheck;

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-4"
      >
        {/* Back button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-2"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Volver a la lista</span>
        </button>

        {/* Member header */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Ranking badge or role icon */}
              {selectedMember.role === 'vendedor' && rankingData.has(selectedMember.user_id) ? (
                (() => {
                  const rankData = rankingData.get(selectedMember.user_id)!;
                  const rankStyles = getRankingStyles(rankData.rank);
                  return (
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${rankStyles.bg} border ${rankStyles.border} flex flex-col items-center justify-center`}>
                      {rankData.rank <= 3 && <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 ${rankStyles.text}`} />}
                      <span className={`font-bold text-sm sm:text-base ${rankStyles.text}`}>#{rankData.rank}</span>
                    </div>
                  );
                })()
              ) : (
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${roleInfo.bgColor} flex items-center justify-center`}>
                  <RoleIcon className={`w-6 h-6 sm:w-7 sm:h-7 ${roleInfo.color}`} />
                </div>
              )}
              <div>
                <h2 className="text-white font-semibold text-lg sm:text-xl">{selectedMember.nombre}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${roleInfo.bgColor} ${roleInfo.color} border ${roleInfo.borderColor}`}>
                    <RoleIcon className="w-3 h-3" />
                    {roleInfo.label}
                  </span>
                  {selectedMember.ubicacion_tienda && (
                    <span className="flex items-center gap-1 text-white/50 text-xs">
                      <MapPin className="w-3 h-3" />
                      {selectedMember.ubicacion_tienda}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleEditMember(selectedMember)}
                className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                title="Editar"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors"
                    title="Eliminar"
                    disabled={deletingId === selectedMember.user_id}
                  >
                    {deletingId === selectedMember.user_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">¿Eliminar {roleInfo.label.toLowerCase()}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/60">
                      Esta acción eliminará permanentemente a <span className="text-white font-medium">{selectedMember.nombre}</span> y todos sus registros.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteMember(selectedMember)}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* PIN info */}
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <span>PIN: <span className="text-white font-mono">{selectedMember.pin}</span></span>
            <span className="text-white/30">·</span>
            <span>Creado: {format(new Date(selectedMember.created_at), 'dd/MM/yyyy', { locale: es })}</span>
          </div>
        </div>

        {/* Stats cards */}
        {loadingDetails ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        ) : memberStats && selectedMember.role === 'vendedor' && rankingData.has(selectedMember.user_id) ? (
          (() => {
            const sellerRankData = rankingData.get(selectedMember.user_id)!;
const score = calculateLegacyScore({
              ventas: memberStats.totalSales,
              canjes: memberStats.totalRedemptions,
              trainingCompleted: sellerRankData.trainingCompleted,
              achievementPoints: sellerRankData.achievementPoints,
            });
            
            return (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {/* Score Total Card - Destacado */}
                <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl p-3 border border-amber-500/30 col-span-2 sm:col-span-1 sm:row-span-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-amber-400" />
                    <span className="text-white/50 text-xs">Score</span>
                  </div>
                  <p className="text-amber-400 font-bold text-2xl">{score.total}</p>
                  <p className="text-white/40 text-[9px] mt-1">puntos</p>
                  <div className="mt-2 space-y-0.5 text-[9px]">
                    <div className="flex justify-between text-white/50">
                      <span>V×2</span>
                      <span className="text-blue-400">+{score.breakdown.ventas}</span>
                    </div>
                    <div className="flex justify-between text-white/50">
                      <span>Canjes</span>
                      <span className="text-purple-400">+{score.breakdown.canjes}</span>
                    </div>
                    <div className="flex justify-between text-white/50">
                      <span>🎓</span>
                      <span className={score.breakdown.training > 0 ? 'text-green-400' : 'text-white/30'}>+{score.breakdown.training}</span>
                    </div>
                    <div className="flex justify-between text-white/50">
                      <span>🏆</span>
                      <span className="text-amber-400">+{score.breakdown.logros}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingBag className="w-4 h-4 text-blue-400" />
                    <span className="text-white/50 text-xs">Ventas</span>
                  </div>
                  <p className="text-white font-semibold text-lg">{memberStats.totalSales}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="text-white/50 text-xs">Monto</span>
                  </div>
                  <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalAmount)}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span className="text-white/50 text-xs">Ahorro</span>
                  </div>
                  <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalSavings)}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Ticket className="w-4 h-4 text-purple-400" />
                    <span className="text-white/50 text-xs">Canjes</span>
                  </div>
                  <p className="text-white font-semibold text-lg">{memberStats.totalRedemptions}</p>
                </div>
              </div>
            );
          })()
        ) : memberStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag className="w-4 h-4 text-blue-400" />
                <span className="text-white/50 text-xs">Ventas</span>
              </div>
              <p className="text-white font-semibold text-lg">{memberStats.totalSales}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-white/50 text-xs">Monto</span>
              </div>
              <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalAmount)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-white/50 text-xs">Ahorro</span>
              </div>
              <p className="text-white font-semibold text-lg">{formatCurrency(memberStats.totalSavings)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Ticket className="w-4 h-4 text-purple-400" />
                <span className="text-white/50 text-xs">Canjes</span>
              </div>
              <p className="text-white font-semibold text-lg">{memberStats.totalRedemptions}</p>
            </div>
          </div>
        )}

        {/* Tabs for sales/redemptions/edit */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10">
          <Tabs value={detailsTab} onValueChange={setDetailsTab}>
            <TabsList className="bg-white/10 border-white/10 mb-4 w-full">
              <TabsTrigger value="ventas" className="flex-1 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                <ShoppingBag className="w-4 h-4 mr-1.5" />
                Ventas
              </TabsTrigger>
              <TabsTrigger value="canjes" className="flex-1 data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
                <Ticket className="w-4 h-4 mr-1.5" />
                Canjes
              </TabsTrigger>
              <TabsTrigger value="logros" className="flex-1 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                <Award className="w-4 h-4 mr-1.5" />
                Logros
              </TabsTrigger>
              <TabsTrigger value="editar" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                <Edit2 className="w-4 h-4 mr-1.5" />
                Editar
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ventas" className="mt-0">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : memberSales.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No hay ventas registradas</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {memberSales.map((sale) => (
                    <div key={sale.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium text-sm truncate">{sale.product_name}</p>
                          <p className="text-white/50 text-xs">{sale.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-medium text-sm">{formatCurrency(sale.final_price)}</p>
                          <p className="text-emerald-400/70 text-xs">+{formatCurrency(sale.savings)} ahorro</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(sale.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        <span className="text-white/20">·</span>
                        <span className="px-1.5 py-0.5 rounded bg-white/10">{sale.category}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="canjes" className="mt-0">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                </div>
              ) : memberRedemptions.length === 0 ? (
                <div className="text-center py-8">
                  <Ticket className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No hay canjes procesados</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {memberRedemptions.map((redemption) => (
                    <div key={redemption.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium text-sm capitalize">{redemption.benefit_type.replace('_', ' ')}</p>
                          <p className="text-white/50 text-xs">{redemption.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-400 font-medium text-sm">x{redemption.quantity}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(redemption.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        {redemption.notes && (
                          <>
                            <span className="text-white/20">·</span>
                            <span className="truncate">{redemption.notes}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="logros" className="mt-0">
              <MemberAchievementsView sellerId={selectedMember.user_id} />
            </TabsContent>

            <TabsContent value="editar" className="mt-0">
              <div className="space-y-4">
                {/* Name Edit */}
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Nombre y Apellido</Label>
                  <Input
                    value={editingId === selectedMember.user_id ? editName : selectedMember.nombre}
                    onChange={(e) => {
                      if (editingId !== selectedMember.user_id) {
                        setEditingId(selectedMember.user_id);
                        setEditName(e.target.value);
                        setEditLocation(selectedMember.ubicacion_tienda || '');
                      } else {
                        setEditName(e.target.value);
                      }
                    }}
                    placeholder="Ej: María López"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>

                {/* Location Edit - Only for sellers */}
                {selectedMember.role === 'vendedor' && (
                  <div className="space-y-2">
                    <Label className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      Tienda que atiende
                    </Label>
                    <Select 
                      value={editingId === selectedMember.user_id ? editLocation : selectedMember.ubicacion_tienda || ''} 
                      onValueChange={(value) => {
                        if (editingId !== selectedMember.user_id) {
                          setEditingId(selectedMember.user_id);
                          setEditName(selectedMember.nombre);
                          setEditLocation(value);
                        } else {
                          setEditLocation(value);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue placeholder="Seleccionar ubicación" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-white/10 z-[200]">
                        {storeLocations.map((location) => (
                          <SelectItem 
                            key={location} 
                            value={location} 
                            className="text-white hover:bg-white/10 focus:bg-white/10"
                          >
                            {location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* PIN Edit */}
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Nuevo PIN (opcional)</Label>
                  <div className="flex justify-center">
                    <PinDisplay value={editingId === selectedMember.user_id ? editPin : ''} variant="blue" maxLength={6} compact />
                  </div>
                  <div className="flex justify-center">
                    <PinKeypad
                      onNumberClick={(num) => {
                        if (editingId !== selectedMember.user_id) {
                          setEditingId(selectedMember.user_id);
                          setEditName(selectedMember.nombre);
                          setEditLocation(selectedMember.ubicacion_tienda || '');
                          setEditPin(num);
                        } else if (editPin.length < 6) {
                          setEditPin(editPin + num);
                        }
                      }}
                      onDelete={() => {
                        if (editingId === selectedMember.user_id) {
                          setEditPin(editPin.slice(0, -1));
                        }
                      }}
                      variant="blue"
                      compact
                    />
                  </div>
                </div>

                {/* Save button */}
                {editingId === selectedMember.user_id && (
                  <div className="flex justify-center pt-2">
                    <Button
                      onClick={() => handleSaveEdit(selectedMember.user_id)}
                      disabled={
                        savingEdit || 
                        !editName.trim() ||
                        (selectedMember.role === 'vendedor' && !editLocation)
                      }
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      {savingEdit ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Guardar Cambios
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="lg:col-span-2 space-y-6">
      <AnimatePresence mode="wait">
        {selectedMember ? (
          <React.Fragment key="details">
            {renderMemberDetails()}
          </React.Fragment>
        ) : (
          <React.Fragment key="list">
            {/* Team Members List - NOW FIRST */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base sm:text-lg">Equipo VillaTech</h3>
                  <p className="text-blue-400/80 text-xs sm:text-sm">
                    {adminCount} admin{adminCount !== 1 ? 's' : ''} · {sellerCount} vendedor{sellerCount !== 1 ? 'es' : ''}
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-3 mb-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por nombre o tienda..."
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                
                {/* Filter row */}
                <div className="flex gap-2">
                  <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as 'todos' | TeamRole)}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white flex-1 h-9 text-xs">
                      <Filter className="w-3 h-3 mr-1 text-white/50" />
                      <SelectValue placeholder="Rol" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 z-[200]">
                      <SelectItem value="todos" className="text-white hover:bg-white/10">Todos los roles</SelectItem>
                      <SelectItem value="admin" className="text-amber-400 hover:bg-white/10">
                        <div className="flex items-center gap-1.5">
                          <Shield className="w-3 h-3" />
                          Administradores
                        </div>
                      </SelectItem>
                      <SelectItem value="vendedor" className="text-purple-400 hover:bg-white/10">
                        <div className="flex items-center gap-1.5">
                          <BadgeCheck className="w-3 h-3" />
                          Vendedores
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white flex-1 h-9 text-xs">
                      <MapPin className="w-3 h-3 mr-1 text-white/50" />
                      <SelectValue placeholder="Tienda" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 z-[200]">
                      <SelectItem value="todas" className="text-white hover:bg-white/10">Todas las tiendas</SelectItem>
                      <SelectItem value="sin-tienda" className="text-white/60 hover:bg-white/10">Sin tienda (Admins)</SelectItem>
                      {uniqueLocations.map((location) => (
                        <SelectItem key={location} value={location} className="text-white hover:bg-white/10">
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filter results count */}
                {(searchQuery || roleFilter !== 'todos' || locationFilter !== 'todas') && (
                  <p className="text-white/50 text-xs">
                    Mostrando {filteredMembers.length} de {teamMembers.length}: {filteredAdminCount} admin{filteredAdminCount !== 1 ? 's' : ''} · {filteredSellerCount} vendedor{filteredSellerCount !== 1 ? 'es' : ''}
                  </p>
                )}
              </div>

              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No hay miembros del equipo</p>
                  <p className="text-white/30 text-xs">Crea el primero usando el formulario abajo</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No se encontraron resultados</p>
                  <p className="text-white/30 text-xs">Probá con otros filtros</p>
                </div>
              ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredMembers.map((member, index) => {
                    const roleInfo = roleLabels[member.role];
                    const RoleIcon = member.role === 'admin' ? Shield : BadgeCheck;
                    const rankData = member.role === 'vendedor' ? rankingData.get(member.user_id) : undefined;

                    return (
                      <motion.button
                        key={member.user_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 * index }}
                        onClick={() => handleSelectMember(member)}
                        className="w-full flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5 hover:border-white/10 transition-colors text-left"
                      >
                        {/* Position number */}
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 font-bold text-sm flex-shrink-0">
                          {rankData?.rank ?? (member.role === 'admin' ? <Shield className="w-4 h-4" /> : index + 1)}
                        </div>
                        
                        {/* Name and location */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{member.nombre}</p>
                          <p className="text-white/40 text-xs truncate">
                            {member.ubicacion_tienda || (member.role === 'admin' ? 'Sin tienda' : 'Sin asignar')}
                          </p>
                        </div>
                        
                        {/* Stats - only for sellers */}
                        {member.role === 'vendedor' && (
                          <div className="flex gap-4 text-sm flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <Ticket className="w-3.5 h-3.5 text-purple-400" />
                              <span className="text-white">{rankData?.canjes ?? 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ShoppingBag className="w-3.5 h-3.5 text-blue-400" />
                              <span className="text-white">{rankData?.ventas ?? 0}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Role badge for admins */}
                        {member.role === 'admin' && (
                          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${roleInfo.bgColor} ${roleInfo.color} border ${roleInfo.borderColor} flex-shrink-0`}>
                            <RoleIcon className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </motion.div>

          </React.Fragment>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamManagementSection;
